'use client';

import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerOrdersDocument } from '@packages/gql/generated/shopping/CustomerOrdersDocument';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import {
  CardHeader,
  Card,
  CardContent,
  CardTitle,
  CardSubtitle,
} from '@packages/shared/src/ui/Card/Card';
import { Select, SelectContent, SelectItem } from '@packages/shared/src/ui/Select/Select';
import { Button } from '@packages/shared/src/ui/Button/Button';
import type {
  CustomerOrderStatus,
  CustomerOrdersQuery,
} from '@packages/gql/generated/shopping/graphql';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Link } from '@packages/modules/src/Link/Link';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { DownPaymentFragmentFragmentDoc } from '@packages/gql/generated/shopping/DownPaymentFragmentFragmentDoc';
import { DownPaymentInlineNotification } from './DownPaymentInlineNotification.client';
import { OrderDetailsButton } from './OrderDetailsButton.client';
import { ProductImageRowList } from '../ProductImageRowList/ProductImageRowList.client';
import { DEFAULT_MAX_AGE_DAYS, PeriodFilter } from '../PeriodFilter/PeriodFilter';
import { OfflineNotification } from '../OfflineNotification/OfflineNotification';

/* GraphQL */ `
  fragment DownPaymentFragment on CustomerOrder {
    downPaymentRequired {
      value
      currencyCode
    }
    paylink
  }
`;

/* GraphQL */ `
  query CustomerOrders(
    $maxAgeDays: Int
    $year: Int
    $status: CustomerOrderStatus
    $paginationKey: String
    $locale: String
  ) {
    customerOrders(
      maxAgeDays: $maxAgeDays
      year: $year
      status: $status
      paginationKey: $paginationKey
      locale: $locale
    ) {
      orders {
        id
        orderNo
        orderDateTime
        orderStatus
        imageUrls
        additionalItemCount
        ...DownPaymentFragment
      }
      nextPaginationKey
    }
  }
`;

type CustomerOrder = NonNullable<CustomerOrdersQuery['customerOrders']>['orders'][number];

const DEFAULT_ORDER_SKELETON_KEYS = Array.from(
  { length: 11 },
  (_, index) => `customer-orders-skeleton-${index}`,
);

const OrdersLoadingSkeleton = () => (
  <div className="bg-color-grey-light my-2">
    {DEFAULT_ORDER_SKELETON_KEYS.map((key) => (
      <Skeleton key={key} className="mb-2 h-[5rem] w-full" />
    ))}
  </div>
);

const OrdersList = ({
  orders,
  statusMapping,
  nextPaginationKey,
  isLastPage,
  onAddPaginationKey,
  showEmptyState,
  bookingsPath,
}: {
  orders: CustomerOrder[];
  statusMapping: Record<CustomerOrderStatus, string>;
  nextPaginationKey?: string | null;
  isLastPage: boolean;
  onAddPaginationKey: (key: string) => void;
  showEmptyState: boolean;
  bookingsPath: string;
}) => {
  const { formatDate } = useIntl();

  const handleLoadMore = () => {
    if (nextPaginationKey) {
      onAddPaginationKey(nextPaginationKey);
    }
  };

  const shouldShowEmptyState = showEmptyState && orders.length === 0;

  return (
    <>
      {shouldShowEmptyState && (
        <Notification severity="info" variant="outline" className="my-2">
          <b>
            <FormattedMessage
              id="CustomerOrders.NoOrders.Headline"
              defaultMessage="Artikel nicht gefunden?"
            />
          </b>
          <br />
          <FormattedMessage
            id="CustomerOrders.NoOrders.Info"
            defaultMessage="Ältere Bestelldaten können aus technischen Gründen leider nicht angezeigt werden. Versendete Artikel findest du aber unter <bookinglink>deinen Buchungen</bookinglink>."
            values={{
              bookinglink: (chunks: React.ReactNode) => (
                <Link href={bookingsPath} className="underline">
                  {chunks}
                </Link>
              ),
            }}
          />
        </Notification>
      )}
      {orders.map((order) => {
        const { downPaymentRequired, paylink } = getFragmentData(
          DownPaymentFragmentFragmentDoc,
          order,
        );

        return (
          <Card key={order.id} className="mb-1.5 last:mb-0">
            <CardHeader hasDivider>
              <div className="flex-col">
                <CardTitle>
                  <FormattedMessage
                    id="CustomerOrders.OrderFrom"
                    defaultMessage="Bestellung vom {date}"
                    values={{
                      date: formatDate(new Date(order.orderDateTime), {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      }),
                    }}
                  />
                </CardTitle>
                <CardSubtitle>
                  <FormattedMessage
                    id="CustomerOrders.Status"
                    defaultMessage="Status: {status}"
                    values={{
                      status: statusMapping[order.orderStatus],
                    }}
                  />
                </CardSubtitle>
              </div>
            </CardHeader>
            {downPaymentRequired && (
              <CardContent>
                <DownPaymentInlineNotification
                  downPaymentRequired={downPaymentRequired}
                  paylink={paylink}
                />
              </CardContent>
            )}

            <CardContent className="flex flex-col justify-between pb-2 md:flex-row md:items-center">
              <ProductImageRowList
                items={order.imageUrls.map((url) => ({ imageUrl: url, name: '', url: null }))}
                additionalItemsCount={order.additionalItemCount}
                keyPrefix={order.id}
                imageSize="large"
              />
              <div className="mt-2 text-center md:mt-0">
                {order.orderNo && <OrderDetailsButton orderNo={order.orderNo} />}
              </div>
            </CardContent>
          </Card>
        );
      })}
      {isLastPage && !!nextPaginationKey && (
        <div className="mt-2 flex justify-center">
          <Button variant="color-button" size="medium" onClick={handleLoadMore}>
            <FormattedMessage
              id="CustomerOrders.LoadMore"
              defaultMessage="Weitere Bestellungen laden"
            />
            <span className="icon-emp-load-reload ml-2 size-4" />
          </Button>
        </div>
      )}
    </>
  );
};

const OrdersPage = ({
  ageFilter,
  statusFilter,
  paginationKey,
  isLastPage,
  onAddPaginationKey,
  statusMapping,
  bookingsPath,
  locale,
}: {
  ageFilter: number;
  statusFilter: 'All' | CustomerOrderStatus;
  paginationKey: string;
  isLastPage: boolean;
  onAddPaginationKey: (key: string) => void;
  statusMapping: Record<CustomerOrderStatus, string>;
  bookingsPath: string;
  locale: string;
}) => {
  const statusVariable = statusFilter === 'All' ? null : statusFilter;
  const [{ data, error }] = useAuthQuery({
    query: CustomerOrdersDocument,
    variables: {
      maxAgeDays: ageFilter,
      year: null,
      status: statusVariable,
      paginationKey,
      locale,
    },
    requestPolicy: 'cache-and-network',
  });

  if (error || data === null) {
    return (
      <Notification severity="error" variant="outline" className="my-2">
        <FormattedMessage
          id="CustomerOrders.Error"
          defaultMessage="Beim Laden der Bestellungen ist ein Fehler aufgetreten."
        />
      </Notification>
    );
  }

  if (!data) {
    const placeholderKeys = Array.from(
      { length: 3 },
      (_, index) => `customer-orders-page-skeleton-${paginationKey}-${index}`,
    );

    return (
      <div className="mt-2">
        {placeholderKeys.map((key) => (
          <Skeleton key={key} className="mb-2 h-[5rem] w-full" />
        ))}
      </div>
    );
  }

  const orders: CustomerOrder[] = data.customerOrders.orders ?? [];

  return (
    <OrdersList
      orders={orders}
      statusMapping={statusMapping}
      nextPaginationKey={data.customerOrders.nextPaginationKey}
      isLastPage={isLastPage}
      onAddPaginationKey={onAddPaginationKey}
      showEmptyState={false}
      bookingsPath={bookingsPath}
    />
  );
};

export const CustomerOrders = () => {
  const { formatMessage, locale } = useIntl();
  const {
    order: {
      paths: { myAccountBookings },
    },
  } = useConfig();
  const [ageFilter, setAgeFilter] = useState(DEFAULT_MAX_AGE_DAYS);
  const [statusFilter, setStatusFilter] = useState<'All' | CustomerOrderStatus>('All');
  const [paginationKeys, setPaginationKeys] = useState<string[]>([]);

  const [{ data, error, fetching }] = useAuthQuery({
    query: CustomerOrdersDocument,
    variables: {
      maxAgeDays: ageFilter,
      year: null,
      status: statusFilter === 'All' ? null : statusFilter,
      paginationKey: undefined,
      locale,
    },
    requestPolicy: 'cache-and-network',
  });

  const handleAddPaginationKey = (key: string) => {
    setPaginationKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const handleAgeFilterChange = (value: number) => {
    setAgeFilter(value);
    setPaginationKeys([]);
  };

  const handleStatusFilterChange = (value: 'All' | CustomerOrderStatus) => {
    setStatusFilter(value);
    setPaginationKeys([]);
  };

  const statusMapping: Record<CustomerOrderStatus, string> = {
    Open: formatMessage({ id: 'CustomerOrders.Status.Open', defaultMessage: 'Offen' }),
    Completed: formatMessage({
      id: 'CustomerOrders.Status.Completed',
      defaultMessage: 'Abgeschlossen',
    }),
  };

  if (error || (data && data?.customerOrders.__typename !== 'CustomerOrdersResult')) {
    return <OfflineNotification />;
  }
  // Handle loading state
  if (fetching || !data) {
    return (
      <div className="bg-color-grey-light my-2">
        {Array.from({ length: 11 }, (_, i) => (
          <Skeleton key={i} className="mb-2 h-[5rem] w-full" />
        ))}
      </div>
    );
  }

  const bookingsPath = getLocalizedPath(myAccountBookings, locale);
  const orders: CustomerOrder[] = data.customerOrders.orders ?? [];

  return (
    <div className="bg-color-grey-light p-1.5 md:p-3">
      <p className="text-size-body-base mb-1 font-semibold">
        <FormattedMessage
          id="CustomerOrders.Headline"
          defaultMessage="Bestellungen schneller finden"
        />
      </p>
      <div className="mb-2 flex w-full justify-between gap-2 lg:w-1/2">
        <PeriodFilter
          value={ageFilter}
          onChange={handleAgeFilterChange}
          label={formatMessage({ id: 'CustomerOrders.LabelAge', defaultMessage: 'Zeitraum' })}
        />
        <Select
          containerClassName="w-full"
          label={formatMessage({
            id: 'CustomerOrders.LabelStatus',
            defaultMessage: 'Sendungsstatus',
          })}
          value={statusFilter}
          onValueChange={(value) => handleStatusFilterChange(value as 'All' | CustomerOrderStatus)}
        >
          <SelectContent>
            <SelectItem value="All">
              <FormattedMessage id="CustomerOrders.Filter.All" defaultMessage="Alle" />
            </SelectItem>
            <SelectItem value="Open">{statusMapping.Open}</SelectItem>
            <SelectItem value="Completed">{statusMapping.Completed}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <OrdersList
        orders={orders}
        statusMapping={statusMapping}
        nextPaginationKey={data.customerOrders.nextPaginationKey}
        isLastPage={paginationKeys.length === 0}
        onAddPaginationKey={handleAddPaginationKey}
        showEmptyState
        bookingsPath={bookingsPath}
      />
      {paginationKeys.map((paginationKey, index) => (
        <OrdersPage
          key={paginationKey}
          ageFilter={ageFilter}
          statusFilter={statusFilter}
          paginationKey={paginationKey}
          isLastPage={index === paginationKeys.length - 1}
          onAddPaginationKey={handleAddPaginationKey}
          statusMapping={statusMapping}
          bookingsPath={bookingsPath}
          locale={locale}
        />
      ))}
    </div>
  );
};
